/**
 * Schedule non-critical work after the page is usable for the user.
 *
 * Triggers on first input/scroll, or a long fallback timeout — not on LCP alone.
 * Loading Sentry / GA / Mixpanel on LCP still lands inside Lighthouse's FCP→TTI
 * TBT window on mobile and tanks the performance score.
 */
export function runAfterPageInteractive(callback: () => void, fallbackMs = 8000): void {
  if (typeof window === 'undefined') return;

  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    cleanup();
    const kick = () => {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(() => callback(), { timeout: 2000 });
      } else {
        setTimeout(callback, 0);
      }
    };
    requestAnimationFrame(() => requestAnimationFrame(kick));
  };

  const onInput = () => run();
  const interactionEvents = ['pointerdown', 'keydown', 'touchstart', 'scroll'] as const;

  const fallback = window.setTimeout(run, fallbackMs);

  const cleanup = () => {
    window.clearTimeout(fallback);
    for (const type of interactionEvents) {
      window.removeEventListener(type, onInput);
    }
  };

  for (const type of interactionEvents) {
    window.addEventListener(type, onInput, { once: true, passive: true });
  }
}
